import { Cookies } from 'quasar'
import { $pegApi } from '@/boot/peg-api'
import { dismissSessionExpired } from '@/boot/fetch'
import { PEG_ADMIN_MARKER_COOKIE } from '@/constants/cookies.const'

/**
 * PEG-509: Internal retailer admin session.
 * Tracks whether the current user is viewing as an internal admin.
 */
export const useAdminStore = defineStore('admin', () => {
  const session = ref(null)
  const checked = ref(false)
  const isAdminMode = computed(() => !!session.value && session.value.mode === 'admin')

  // JS-readable marker cookie set alongside the HttpOnly session cookie
  // during /v1/admin/launch. Gates FE admin-session calls so normal users
  // never hit /admin/session/me.
  function hasAdminMarker() {
    return !!Cookies.get(PEG_ADMIN_MARKER_COOKIE)
  }

  function clearMarker() {
    Cookies.remove(PEG_ADMIN_MARKER_COOKIE, { path: '/' })
  }

  /**
   * Detect `peg_admin=corelink` query param (set by /v1/admin/launch redirect),
   * persist as a same-domain marker cookie, and strip the param from the URL.
   */
  function initMarkerFromUrl(router) {
    const route = router.currentRoute.value
    if (route.query.peg_admin === 'corelink') {
      Cookies.set(PEG_ADMIN_MARKER_COOKIE, 'corelink', { secure: true, expires: '8h', path: '/' })
      const query = { ...route.query }
      delete query.peg_admin
      router.replace({ path: route.path, query })
    }
  }

  async function fetchSession() {
    try {
      const resp = await $pegApi.AdminDashboard.getSession()
      session.value = resp?.mode === 'admin' ? resp : null
    } catch {
      session.value = null
    } finally {
      checked.value = true
    }
  }

  /** Called when a 401 on an /admin endpoint while the marker cookie is present indicates the admin session expired. */
  function handleSessionExpired() {
    session.value = null
    checked.value = true
    clearMarker()
    dismissSessionExpired()
  }

  async function logout() {
    try {
      await $pegApi.AdminDashboard.logout()
    } catch {
      // best-effort
    }
    session.value = null
    clearMarker()
    dismissSessionExpired()
  }

  return { session, checked, isAdminMode, hasAdminMarker, initMarkerFromUrl, fetchSession, handleSessionExpired, logout }
})
